import { useEffect, useRef, useState } from "react";
import Lock from "@assets/images/mdi_lock.svg";
import { ChevronDown } from "lucide-react";
import { Checkbox } from "@mui/material";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const MedicalViewMultipSelectDropdown = ({
  options,
  selectedValues,
  onSelect,
  placeholder,
  isEditable,
  isFistForm = false,
  label = "",
  hasError = false,
  sleepCoach = false,
  maxSelection = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownDirection, setDropdownDirection] = useState("down");
  const selectedDiet = localStorage.getItem("selectedDiet");

  const dropdownRef = useRef(null);
  const { t } = useTranslation();

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const calculateDropdownDirection = () => {
    if (!dropdownRef.current) return "down";
    
    const dropdownRect = dropdownRef.current.getBoundingClientRect();
    const dropdownHeight = 280; // Approximate max height of dropdown (search + options)
    
    // Space available below the dropdown trigger
    const spaceBelow = window.innerHeight - dropdownRect.bottom;
    
    // Space available above the dropdown trigger  
    const spaceAbove = dropdownRect.top;
    
    // Open upward if not enough space below AND more space is available above
    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
      return "up";
    }
    
    // Default to opening downward
    return "down";
  };

  const getTranslatedLabel = (value) => {
    const option = options?.find((opt) => opt.value === value);
    return option ? option.label : value;
  };

  const toggleOption = (optionValue) => {
    if (selectedValues.includes(optionValue)) {
      onSelect(selectedValues.filter((item) => item !== optionValue));
      if (optionValue.toLowerCase() === "carnivore") {
        localStorage.removeItem("selectedDiet");
      }
    } else {
      if (maxSelection && selectedValues.length >= maxSelection) {
        toast.error(t("The field cannot have values more than 2."));
        return;
      }
      if (optionValue.toLowerCase() === "carnivore") {
        localStorage.setItem("selectedDiet", optionValue);
      }
      onSelect([...selectedValues, optionValue]);
    }
  };

  const filteredOptions = options
    ?.filter((option) => {
      if (
        selectedDiet?.toLowerCase() === "carnivore" &&
        ["vegetarian european", "vegan global"].includes(option.value.toLowerCase())
      ) {
        return false;
      }
      return true;
    })
    .filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const getSelectedOptionLabels = () => {
    return selectedValues?.map((value) => getTranslatedLabel(value));
  };

  const handleToggleDropdown = () => {
    if (!isEditable) return;
    
    if (isOpen) {
      setSearchTerm("");
      setIsOpen(false);
    } else {
      const direction = calculateDropdownDirection();
      setDropdownDirection(direction);
      setIsOpen(true);
    }
  };

  return (
    <div
      className={`relative ${isFistForm ? "w-full" : "w-[90%]"}`}
      ref={dropdownRef}
    >
      <div
        className={`border p-2 pr-[0.6rem] rounded-md w-full h-10 flex items-center justify-between bg-[#fff] ${
          !isEditable ? "cursor-default" : "cursor-pointer"
        } ${
          hasError ? "border-red-500 placeholder-red-400" : "border-gray-300"
        }`}
        onClick={handleToggleDropdown}
      >
        <span
          className={`truncate text-[12px] ${
            hasError && selectedValues.length === 0
              ? !isFistForm && "text-red-400"
              : "text-black"
          }`}
        >
          {selectedValues.length > 0
            ? getSelectedOptionLabels()[getSelectedOptionLabels().length - 1]
            : placeholder}
        </span>
       
          <ChevronDown
            className={`w-[14px] h-[14px] transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
            color="black"
            strokeWidth={4}
          />
      </div>

      {isOpen && isEditable && (
        <div 
          className={`absolute z-50 w-full bg-white border border-gray-300 rounded-md shadow-lg overflow-hidden ${
            dropdownDirection === "up" 
              ? "bottom-full mb-1" 
              : "top-full mt-1"
          }`}
          style={{
            maxHeight: dropdownDirection === "up" 
              ? `${Math.min(280, dropdownRef.current?.getBoundingClientRect().top - 10)}px`
              : "280px"
          }}
        >
          {/* Search Input */}
          <div className="sticky top-0 bg-white px-3 py-2 border-b z-10">
            <input
              type="text"
              placeholder={`${t("search")}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              autoFocus
            />
          </div>

          {/* Filtered Options with proper scrolling */}
          <div 
            className="overflow-y-auto"
            style={{
              maxHeight: dropdownDirection === "up"
                ? `${Math.min(220, dropdownRef.current?.getBoundingClientRect().top - 70)}px`
                : "220px"
            }}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className={`px-4 py-2 hover:bg-blue-50 cursor-pointer transition-colors ${
                    selectedValues.includes(option.value)
                      ? "bg-blue-100 border-l-2 border-l-blue-500"
                      : "bg-white"
                  }`}
                  onClick={() => toggleOption(option.value)}
                >
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={selectedValues.includes(option.value)}
                      readOnly
                      color="primary"
                      size="small"
                      sx={{ 
                        padding: "0",
                        '&.Mui-checked': {
                          color: '#1976d2',
                        },
                      }}
                    />
                    <span className="text-sm">{option.label}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                {t("No options found")}
              </div>
            )}
          </div>
        </div>
      )}

      {hasError && (
        <div className="text-red-500 text-[10px] md:text-[12px] ml-2 mt-1">
          {sleepCoach
            ? "This field is required"
            : t("validation.required", { field: label })}
        </div>
      )}
    </div>
  );
};

export default MedicalViewMultipSelectDropdown;